<template>
<div
  class="card-wrap__inner rule-campaigns__content create-campaign-dialog-flow-path mb-10"
  :class="{'card_wrap__inner_inactive': path.status === 'inactive'}"
>
  <div class="card-wrap__inner-head create-campaign-dialog-flow-path__header">
    <ui-input
      v-if="reportingFlowRulePathEnabled"
      class="path-name"
      v-model="path.name"
    />
    <div v-if="!reportingFlowRulePathEnabled" class="path_name">{{ translatedName(path.name || '') }}</div>
    <div class="inner-head-right">
      <template v-if="path.status === 'active'">
        <span class="path_weight path_weight__name">{{ t('Weight') }}:</span>
        <ui-input
          class="input-weight"
          v-model="paths[pathIndex].weight"
          min="0"
          :disabled="isDisabledWeight"
          :error-messages="weightErrorMessages"
        ></ui-input>

        <ui-tooltip-icon
          v-if="activeSwitchDisabled"
          preset="warning"
          :text="t('Weight = 0, the Path is disabled')"
        ></ui-tooltip-icon>

        <div class="rule-campaigns__header-action-block" :class="{ disabled: isDisabledWeight }">
          <span class="path_weight">({{ countWeight(paths[pathIndex], paths) }}%)</span>
        </div>

        <div
          v-if="paths.length > 1"
          class="rule-campaigns__header-action-block"
        >
          <ui-icon
            class="path_del cursor-pointer"
            size="14"
            :color="$colors.red"
            @click="deletePath"
            icon="fas fa-trash-alt"
          ></ui-icon>
        </div>
      </template>
      <div class="rule-campaigns__header-action-block rule-campaigns__header-switch">
        <ui-switcher
          v-model="active"
          true-value="active"
          false-value="inactive"
          :disabled="activeSwitchDisabled"
        ></ui-switcher>
      </div>
    </div>
  </div>
  <div
    v-if="path.status === 'active'"
    class="path_content">
    <div class="mb-6">
      <div class="div-table__row div-table__row--header">
        <div class="d-flex align-center">
          <ui-switcher
            v-model="path.directLinking"
            class="align-items-center"
            :true-value="false"
            :false-value="true"
            :labelHelp="landingDisabledInStateActive.includes(form.rotation) && t('With selected type of traffic distribution the landing should be set up in campaign.')"
            :disabled="activeSwitchDisabled || landingDisabledInStateActive.includes(form.rotation)"
            :label="`# ${t('Landings')}`"
          >
          </ui-switcher>
          <ui-icon
            v-if="isEmptyLandingsError && !path.directLinking"
            :color="$colors.tangerine"
            class="ml-4"
            icon="fas fa-exclamation-triangle"
          ></ui-icon>
        </div>
        <span class="create-campaign-dialog-flow-path__weight">{{ t('Weight') }}</span>
      </div>
      <div
        v-if="path.landings.length === 0 && !path.directLinking"
        class="div-table__row"
      >
        <div
          class="text-center"
        >{{ t('No landings added') }}</div>
      </div>
      <div v-if="path.directLinking" class="div-table__row">
        <div
          class="text-center"
        >{{ t('Direct linking enabled') }}</div>
      </div>

      <create-campaign-dialog-flow-landing
        v-for="(landing, landingIndex) in path.landings"
        :isDisabledWeight="isDisabledWeight"
        :key="landingIndex"
        :landing="landing"
        :direct-linking="path.directLinking"
        :rule-index="ruleIndex"
        :path-index="pathIndex"
        :landing-index="landingIndex"
        :total-weight="totalWeightOfLandings"
        :path="path"
        :form="form"
        :errors-raw="errorsRaw"
        :error-condition-prefix="errorConditionPrefix"
        :dialogRepositionParam="dialogRepositionParam"
        @delete="delLanding(landingIndex)"
        @edit="editLanding(landing.id)"
        @fix-landing-count="fixLandingCount"
        @preview="previewLanding"
        :landingOptions="landingOptions"
      ></create-campaign-dialog-flow-landing>

      <div
        v-if="!path.directLinking"
        class="create-campaign-dialog-flow-path__footer-actions"
      >
        <button
          :disabled="!aclCanCreateOnSection({
            workspaceId: form.workspaceId,
            section: 'landings',
          })"
          @click="addNewLanding()"
          type="button" class="create-campaign-dialog-flow-path__footer-action"
        >
          {{ t('Add new landing') }}
          <ui-plan-limits
            v-if="hasPlanLimitsRestriction('landingsAmount')"
            class="ml-2"
          ></ui-plan-limits>
        </button>
        <button type="button"
          class="create-campaign-dialog-flow-path__footer-action"
          @click="addLanding()"
        >
           {{ t('+ Add landing') }}
        </button>
      </div>
    </div>
    <div>
      <div class="div-table__row div-table__row--header">
        <div class="d-flex align-center">
          <span># {{ t('Offers') }}</span>
          <ui-icon
            :color="$colors.tangerine"
            v-if="isEmptyOffersError"
            class="ml-4"
            icon="fas fa-exclamation-triangle"
          ></ui-icon>
        </div>
        <span class="create-campaign-dialog-flow-path__weight">{{ t('Weight') }}</span>
      </div>
      <div v-if="path.offers.length === 0" class="div-table__row">
        <div
          class="text-center"
        >{{ t('No offers added') }}</div>
      </div>
      <create-campaign-dialog-flow-offer
        v-for="(offer, offerIndex) in path.offers"
        :key="offerIndex"
        :offerOptions="offerOptions"
        :isDisabledWeight="isDisabledWeight"
        :offer="offer"
        :country-id="countryId"
        :offers-count="offersCount"
        :rule-index="ruleIndex"
        :path-index="pathIndex"
        :offer-index="offerIndex"
        :total-weight="totalWeightOfOffers"
        :path="path"
        :form="form"
        :errors-raw="errorsRaw"
        :error-condition-prefix="errorConditionPrefix"
        :dialogRepositionParam="dialogRepositionParam"
        @delete="delOffer(offerIndex)"
        @edit="editOffer(offer.id)"
        @preview="previewOffer"
      ></create-campaign-dialog-flow-offer>
      <div class="create-campaign-dialog-flow-path__footer-actions">
        <button
          type="button"
          @click="addNewOffer"
          :disabled="!aclCanCreateOnSection({
            workspaceId: form.workspaceId,
            section: 'offers',
          })"
          class="create-campaign-dialog-flow-path__footer-action">
          {{ t('Add new offer') }}
          <ui-plan-limits
            v-if="hasPlanLimitsRestriction('offersAmount')"
            class="ml-2"
          ></ui-plan-limits>
        </button>
        <button
          type="button"
          v-if="offersCount <= 1"
          class="create-campaign-dialog-flow-path__footer-action"
          @click="addOffer(1)"
        >
          {{ t('+ Add offer') }}
        </button>
      </div>
    </div>
  </div>
</div>
</template>

<script setup lang="ts">
import { useColors } from '@/composables';
import CreateCampaignDialogFlowLanding from './create-campaign-dialog-flow-landing.vue';
import CreateCampaignDialogFlowOffer from './create-campaign-dialog-flow-offer.vue';
import { useCampaignFlowPath } from './useCampaignFlowPath';

type FlowPathItem = {
  name?: string;
  weight: number;
  status?: string;
  directLinking?: boolean;
  landings: Array<{ id: string; weight?: number; offers?: number }>;
  offers: Array<{ id: string; weight: number }>;
};

const $colors = useColors();

const props = defineProps<{
  path: FlowPathItem;
  isDisabledWeight: boolean;
  paths: FlowPathItem[];
  ruleIndex: number;
  pathIndex: number;
  form: Record<string, unknown> & {
    workspaceId: string;
    rotation: string;
  };
  countryId: string;
  errorConditionPrefix: string[];
  dialogRepositionParam: string;
  errorsRaw: unknown[][] | null;
  offerOptions: Offer[];
  landingOptions: Landing[];
}>();

const {
  t,
  reportingFlowRulePathEnabled,
  landingDisabledInStateActive,
  activeSwitchDisabled,
  active,
  offersCount,
  totalWeightOfLandings,
  totalWeightOfOffers,
  isEmptyLandingsError,
  isEmptyOffersError,
  weightErrorMessages,
  hasPlanLimitsRestriction,
  aclCanCreateOnSection,
  addOffer,
  addNewOffer,
  editOffer,
  previewOffer,
  delOffer,
  addLanding,
  addNewLanding,
  editLanding,
  previewLanding,
  delLanding,
  fixLandingCount,
  countWeight,
  deletePath,
  translatedName,
} = useCampaignFlowPath(props);
</script>

<style lang="scss">
.create-campaign-dialog-flow-path {
  &__weight {
    width: 170px;
  } 

  &__footer-actions {
    display: flex;
    background: var(--color-white);
  }

  &__footer-action {
    flex-grow: 1;
    text-align: center;
    padding: 12px 10px;
    color: var(--color-green);
    font-size: 13px;
    &:disabled{
      color:var(--color-gray5);
    }

    & + & {
      border-left: 2px solid var(--color-gray10);
    }
  }

  &__header {
    padding: 10px 0;
  }
}
</style>

<style lang="scss" scoped>
.content-container {
  overflow-x: auto;
}
.inner-head-right {
  white-space: nowrap;
}

.input-weight {
  width: 60px;
  margin: 0 8px;
}

@media only screen and (max-width: 450px) {
  .path_weight__name {
    max-width: 42px;
    line-height: 1;
    word-break: break-all;
    margin-left:12px;
  }
}
</style>
